<template>
  <div class="table-container" id="data-list">
    <div v-if="isLoading" class="spinner-overlay">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <table class="ms-table table table-sm">
      <thead class="NKT-P1-medium sticky-header">
        <tr>
          <th class="sort" data-sort="name" scope="col">
            <a href="#" class="text-body-secondary list-sort">Name</a>
          </th>
          <th class="sort" data-sort="sub-category" scope="col">
            <a href="#" class="text-body-secondary list-sort">Subcategory</a>
          </th>
          <th class="sort" data-sort="owner" scope="col">
            <a href="#" class="text-body-secondary list-sort">Owner</a>
          </th>
          <th class="sort" data-sort="relations" scope="col">
            <a href="#" class="text-body-secondary list-sort">Relations</a>
          </th>
          <th class="sort" data-sort="size" scope="col">
            <a href="#" class="text-body-secondary list-sort">Size</a>
          </th>
          <th class="sort" data-sort="last-modified" scope="col">
            <a href="#" class="text-body-secondary list-sort">Last Modified</a>
          </th>
          <th scope="col" style="width: 80px"></th>
        </tr>
      </thead>
      <tbody class="list" v-if="!isLoading" :key="filesVersion">
        <tr
          class="ms-row"
          v-for="(file, index) in files"
          :key="file.id"
          :file-row-index="index"
          :id="`fileRow-${index}`"
        >
          <td class="name" scope="row" :id="`fileRow-${index}-name`">
            {{ file.name }}
          </td>
          <td :id="`fileRow-${index}-subcategory`" class="sub-category">
            <div class="d-flex flex-wrap align-items-center">
              {{ getName(file.subcategory) }}
              <span
                class="subcategory-active NKT-P2 ms-2"
                v-if="
                  file.subcategory === 'Marine survey data file' ||
                  file.subcategory === 'Inventory list' ||
                  file.subcategory === 'Cable shape file'
                "
              >
                Active
              </span>
            </div>
          </td>

          <td :id="`fileRow-${index}-owner`" class="owner">
            {{ file.fileOwner?.name }}
          </td>

          <td :id="`fileRow-${index}-relationType`" class="relations">
            {{ file.relationType?.map((x) => getRelationDisplayName(x)).join(', ') }}
          </td>

          <td :id="`fileRow-${index}-fileSize`" class="size" :data-order="file.fileSize ?? 0">
            {{ formatFileSize(file.fileSize ?? 0) }}
          </td>

          <td :id="`fileRow-${index}-timestamp`" class="last-modified" :data-order="file.timestamp?.getTime() ?? 0">
            {{ formatTimestamp(file.timestamp) }}
          </td>

          <td>
            <span
              v-if="showDelete && userStore.isOwnerOrHigher"
              @click="() => clickDeleteFile(file)"
              class="small-icon ms-icon"
              :class="{
                'spinner-border spinner-border-sm': deletingFile === file,
                'fe fe-trash-2 ': deletingFile !== file
              }"
              style="position: relative"
            >
              <InlineConfirmationDialog
                :show="deletingFile !== file && showDeleteFile === file.id"
                @cancel="cancel"
                @confirm="() => deleteFile(file)"
                :title="'Are you sure you want to delete?'"
                :show-comment="false"
              >
              </InlineConfirmationDialog>
            </span>
            <a @click="downloadFile(file)" target="_blank">
              <span
                class="small-icon ms-icon"
                :class="{
                  'spinner-border spinner-border-sm': downloadingFile === file,
                  'fe fe-download ': downloadingFile !== file
                }"
              ></span>
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import {
  DocumentationClient,
  SparePartsClient,
  SurveyClient,
  File,
  RelationType
} from '@/server/MakeSenseApp-backend'
import { useProjectStore } from '@/stores/ProjectStore'
import InlineConfirmationDialog from '@/components/dialogs/InlineConfirmationDialog.vue'
import moment from 'moment'
import { defineProps, ref, PropType, computed, watch, onMounted, nextTick } from 'vue'
import { useDocumentationStore } from '@/stores/DocumentationStore'
import { SubCategoryValues } from '@/utilities/subCategoryValues'
import { RelationValues } from '@/utilities/relationsValues'
import '@/styles/ms-table-styles.scss'
import { requiredInject } from '@/utilities/requiredInject'
import { useUserStore } from '@/stores/UserStore'
import List from 'list.js'

const props = defineProps({
  files: {
    type: Array as PropType<File[]>,
    default: () => []
  },
  isLoading: Boolean,
  showDelete: Boolean
})

var showDeleteFile = ref<string | undefined>('')

const documentationClient = requiredInject<DocumentationClient>('DocumentationClient')
const surveyClient = requiredInject<SurveyClient>('SurveyClient')
const sparePartsClient = requiredInject<SparePartsClient>('SparePartsClient')

var projectStore = useProjectStore()
var documentationStore = useDocumentationStore()

var deletingFile = ref<File | undefined>(undefined)
var downloadingFile = ref<File | undefined>(undefined)

const userStore = useUserStore()
const files = computed(() => props.files)
const filesVersion = ref(0)

function getName(subcategory: string | undefined): string {
  if (!subcategory) return '-'

  if (subcategory === 'Marine survey data file') return subcategory

  if (subcategory === 'Inventory list') return subcategory

  if (subcategory === 'Cable shape file') return subcategory

  return SubCategoryValues.find((x) => x.value === subcategory)?.text ?? '-'
}

function getRelationDisplayName(relation: RelationType) {
  return RelationValues.find((x) => x.value === relation)?.text ?? '-'
}

function clickDeleteFile(file: File) {
  showDeleteFile.value = file.id
}

let documentationFileList: List | null = null

const sortFiles = () => {
  const options = {
    valueNames: [
      'name',
      'owner',
      'relations',
      { name: 'size', attr: 'data-order' },
      { name: 'last-modified', attr: 'data-order' },
      'sub-category'
    ]
  }

  documentationFileList = new List('data-list', options)

  if (documentationFileList.items.length > 0) {
    documentationFileList.sort('name', { order: 'asc' })
  }
}

// Watch for changes in `files` to handle async updates
onMounted(() => {
  watch(
    files,
    async (newFiles) => {
      filesVersion.value++
      if (newFiles.length > 0) {
        await nextTick() // ensure DOM updates are done
        sortFiles()
      }
    },
    { immediate: true }
  )
})

function cancel() {
  setTimeout(() => {
    showDeleteFile.value = undefined
  }, 1)
}

function deleteFile(file: File) {
  deletingFile.value = file

  if (file.relatedItem && file.subcategory === 'Marine survey data file') {
    surveyClient.deleteSurvey(projectStore.projectId, file.relatedItem)
  }

  if (file.relatedItem && file.subcategory === 'Inventory list') {
    sparePartsClient.deleteSparePart(file.relatedItem)
  }

  documentationClient
    .deleteFile(projectStore.projectId, file.id!)
    .then(() => {
      showDeleteFile.value = undefined
      documentationStore.reloadFiles()
      deletingFile.value = undefined
    })
    .catch((error) => {
      console.error(error)
      deletingFile.value = undefined
    })
}

const formatFileSize = (sizeInBytes: number): string => {
  const i = Math.floor(Math.log(sizeInBytes) / Math.log(1024))
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const size = (sizeInBytes / Math.pow(1024, i)).toFixed(2)
  return `${size} ${sizes[i]}`
}

const formatTimestamp = (timestamp: Date | undefined): string => {
  if (!timestamp) return '-'
  return moment(timestamp).format('yyyy-MM-DD HH:mm')
}

const downloadFile = async (file: File) => {
  try {
    downloadingFile.value = file
    const response = await documentationClient.downloadFile(projectStore.projectId, file.id!)

    const blob = response.data
    const url = URL.createObjectURL(blob)

    const link: any = document.createElement('a')
    link.href = url
    link.download = file.name // Use the file's name

    link.click()

    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading file:', error)
  }

  downloadingFile.value = undefined
}
</script>

<style scoped lang="scss">
.table-container {
  position: relative;
}

.table thead th {
  letter-spacing: normal;
}

.subcategory-active {
  display: flex;
  padding: 0px 4px;
  width: 47px;
  height: 16px;
  justify-content: center;
  align-items: center;

  background: var(--color-components-label-tag-selected, #c8f6c8);
}

thead th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1000;
}

.table-container {
  max-height: 700px;
  overflow-y: auto;
  overflow-x: auto;
}

.ms-table {
  position: sticky;
  z-index: 0;
}
</style>
